
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Preloader: React.FC = () => {
  const [text, setText] = useState('INITIALIZING SYSTEM...');
  const [progress, setProgress] = useState(0);

  const phrases = [
    'ENCRYPTING DATA STREAM...',
    'NEURAL INTERFACE CONNECTED',
    'OPTIMIZING ARCHITECTURE...',
    'READY TO NAVIGATE'
  ];

  useEffect(() => {
    let phraseIdx = 0;
    const phraseInterval = setInterval(() => {
      if (phraseIdx < phrases.length) {
        setText(phrases[phraseIdx]);
        phraseIdx++;
      }
    }, 500);

    const progressInterval = setInterval(() => {
      setProgress(prev => (prev < 100 ? prev + 1.2 : 100));
    }, 20);

    return () => {
      clearInterval(phraseInterval);
      clearInterval(progressInterval);
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 1.1, filter: 'blur(20px)' }}
      transition={{ duration: 0.8, ease: "easeInOut" }}
      className="fixed inset-0 z-[1000] bg-[#121212] flex flex-col items-center justify-center p-6 overflow-hidden"
    >
      {/* Background digital noise effect */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" 
           style={{ backgroundImage: 'radial-gradient(#39FF14 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
      
      {/* Scanning line */}
      <motion.div 
        animate={{ y: ['-100%', '100%'] }}
        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        className="absolute inset-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#39FF14]/30 to-transparent z-10"
      />

      <div className="relative z-20 flex flex-col items-center max-w-sm w-full">
        {/* Animated Bracket Logo */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 border-2 border-t-[#39FF14] border-r-[#39FF14]/20 border-b-[#39FF14]/10 border-l-[#39FF14]/5 rounded-full mb-8 relative flex items-center justify-center"
        >
          <div className="w-8 h-8 bg-[#39FF14]/10 rounded-full flex items-center justify-center">
            <div className="w-2 h-2 bg-[#39FF14] rounded-full shadow-[0_0_10px_#39FF14]" />
          </div>
        </motion.div>

        {/* Status Text */}
        <div className="text-center mb-6">
          <motion.div
            key={text}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-syncopate text-[10px] md:text-xs font-bold text-[#39FF14] tracking-[0.4em] uppercase mb-2 h-4"
          >
            {text}
          </motion.div>
          <div className="text-[9px] font-mono text-gray-600 font-bold uppercase tracking-widest">
            JUANMA TENA // CORE_VERSION_3.1.0
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full h-[2px] bg-white/5 relative overflow-hidden rounded-full mb-4">
          <motion.div 
            style={{ width: `${progress}%` }}
            className="h-full bg-[#39FF14] shadow-[0_0_10px_#39FF14]"
          />
        </div>
        
        <div className="flex justify-between w-full font-mono text-[8px] text-[#39FF14]/40 font-bold uppercase">
          <span>Booting Protocol</span>
          <span>{Math.floor(progress)}%</span>
        </div>
      </div>

      {/* Subtle Corner Decorations */}
      <div className="absolute top-10 left-10 w-20 h-20 border-t border-l border-[#39FF14]/20" />
      <div className="absolute bottom-10 right-10 w-20 h-20 border-b border-r border-[#39FF14]/20" />
    </motion.div>
  );
};

export default Preloader;
